"use client";

import React, { useEffect, useState, useRef, useCallback } from "react";
import { signOut, useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { Trans } from "@lingui/macro";
import UserSvg from "@/components/svg/header/user";
import WalletSvg from "@/components/svg/header/wallet";
import BagSvg from "@/components/svg/header/bag";
import CashSvg from "@/components/svg/header/cash";
import HeartSvg from "@/components/svg/header/heart";
import LogoutSvg from "@/components/svg/header/logout";
import { useMutation } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { getRequest, postRequest } from "@/utils/request";

interface WishlistProps {
  product_id: string;
  product_seo_url: string;
  thumb: string;
  name: string;
  model: string;
  stock: string;
  price: string;
  special: string;
}

const menuItems = [
  { label: <Trans>My account</Trans>, path: "/account", Icon: UserSvg, isWishlist: false },
  {
    label: <Trans>My Wallet</Trans>,
    path: "/account/voucherwallet",
    Icon: WalletSvg,
    isWishlist: false,
  },
  { label: <Trans>Order History</Trans>, path: "/account/orders", Icon: BagSvg, isWishlist: false },
  {
    label: <Trans>Transactions</Trans>,
    path: "/account/transactions",
    Icon: CashSvg,
    isWishlist: false,
  },
  { label: <Trans>Offerz Plus</Trans>, path: "/loyalty", Icon: CashSvg, isWishlist: false },
  { label: <Trans>Wish List</Trans>, path: "/account/wishlist", Icon: HeartSvg, isWishlist: true },
];

export default function HeaderUserMenu() {
  const [pendingToggle, setPendingToggle] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { data: session, status } = useSession();
  const router = useRouter();
  const lang = Cookies.get("locale");

  useEffect(() => {
    const dataFetch = async () => {
      try {
        const response = await getRequest({
          path: { name: "wishlist" },
          lang,
          skipCache: true,
        });

        if (response?.data && Array.isArray(response.data)) {
          const wishlistArray = response.data.map((product: WishlistProps) => product.product_id);
          if (status === "unauthenticated") {
            Cookies.set("anon-wishlist", JSON.stringify(wishlistArray));
          } else {
            Cookies.set("wishlist", JSON.stringify(wishlistArray));
          }
        }
      } catch (err) {
        console.warn("Error fetching wishlist:", err);
      }
    };
    dataFetch();
  }, [lang]);

  const logoutMutation = useMutation({
    mutationFn: (data: object) =>
      postRequest({ path: { name: "logout" }, body: data, method: "POST", lang }),
  });

  const handleToggle = () => {
    if (status === "loading") {
      setPendingToggle(true);
    } else {
      handleSession();
    }
  };

  const handleSession = useCallback(async () => {
    if (session && session.user?.is_guest === "1") {
      await signOut({ callbackUrl: "/login", redirect: true });
    } else {
      if (!session || (session && new Date(session.expires) < new Date())) {
        router.push("/login");
      } else {
        setMenuOpen((prev) => !prev);
      }
    }
  }, [router, session]);

  useEffect(() => {
    if (pendingToggle && status !== "loading") {
      setPendingToggle(false);
      handleSession();
    }
  }, [pendingToggle, status, session, router, handleSession]);

  const wishlist = Cookies.get("wishlist");
  const wishlistArray = wishlist ? JSON.parse(wishlist) : [];

  const handleMenuItemClick = (path: string) => {
    router.push(path);
    setMenuOpen(false);
  };

  const handleLogout = () => {
    logoutMutation.mutate(
      {},
      {
        onError: () => {
          signOut({ callbackUrl: "/signout" });
        },
        onSuccess: () => {
          signOut({ callbackUrl: "/signout" });
        },
      },
    );
  };

  // Close the dropdown when clicking outside
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <div className="relative inline-flex">
      <div
        onClick={handleToggle}
        className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg md:h-[42px] md:w-[42px] md:hover:bg-[#eeeef0]"
      >
        <UserSvg />
      </div>

      {menuOpen && (
        <div
          ref={menuRef}
          className="absolute right-0 z-10 mt-10 w-[215px] rounded border border-gray-200 bg-white shadow-lg"
        >
          <ul>
            {menuItems
              .filter(({ path }) => {
                if (
                  path === "/loyalty" &&
                  session?.user &&
                  parseInt(session?.user?.customer_group_id) ===
                    Number(process.env.NEXT_PUBLIC_BUSSINESS_CUSTOMER_GROUP)
                ) {
                  return false;
                }
                return true;
              })
              .map(({ label, path, Icon, isWishlist }, index) => (
                <li
                  key={index}
                  onClick={() => handleMenuItemClick(`/${lang}${path}`)}
                  className="flex min-w-[215px] cursor-pointer items-center border-b border-[#E0e0e0] p-[14px] hover:text-primary"
                >
                  <div className="min-w-8">
                    <Icon />
                  </div>
                  <span className="text-[17px] transition-all duration-300 ease-in-out">
                    {label}
                    {isWishlist ? ` (${wishlistArray.length})` : ""}
                  </span>
                </li>
              ))}
            <li
              onClick={handleLogout}
              className="flex cursor-pointer items-center p-[14px] hover:text-primary"
            >
              <div className="mr-2">
                <LogoutSvg />
              </div>
              <span className="text-[17px]">
                <Trans>Logout</Trans>
              </span>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
